import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { marketplaceApi } from '../services/api';
import { saveErrorText } from '../utils/apiError';
import { ExpertCard, SavedExpertAvatar, type ExpertCardStatus } from './ExpertCard';
import { ExpertFinder } from './ExpertFinder';
import type { Expert } from '../utils/expertProfile';
import Modal from './Modal';
import { ProviderRequestFields, emptyProviderRequestDraft, isProviderRequestDraftComplete } from './ProviderRequestFields';
import type { Lawyer, Inspector, ProviderRequest } from '../types';

interface MarketplaceTabProps {
  projectId: string;
}

export function MarketplaceTab({ projectId }: MarketplaceTabProps) {
  const { t } = useTranslation();
  // The kind of expert shown under "Hitta hjälp", as on Extern hjälp (ClickUp 869fk17e6).
  const [type, setType] = useState<'lawyer' | 'inspector'>('lawyer');
  const [openProfile, setOpenProfile] = useState<string | null>(null);
  const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [selectedProviderType, setSelectedProviderType] = useState<'lawyer' | 'inspector' | null>(null);
  const [expandedPresentation, setExpandedPresentation] = useState<string | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState(emptyProviderRequestDraft);
  const [requestSent, setRequestSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const requestIdempotencyKey = useRef<string | null>(null);
  const submissionInFlight = useRef(false);

  // Existing requests for this project
  const [existingRequests, setExistingRequests] = useState<ProviderRequest[]>([]);
  const [requestsLoaded, setRequestsLoaded] = useState(false);

  // "Bjud in till projekt" on Extern hjälp lands here with ?provider=&providerType=:
  // that actor's service is opened, the actor chosen and the request form shown
  // (or, with a request already sent, its card with "Bjud in till projekt").
  const [searchParams, setSearchParams] = useSearchParams();
  const preselect = useRef<{ id: string; type: 'lawyer' | 'inspector' } | null>(null);
  useEffect(() => {
    const id = searchParams.get('provider');
    const type = searchParams.get('providerType');
    if (!id || (type !== 'lawyer' && type !== 'inspector')) return;
    preselect.current = { id, type };
    setType(type);
    const next = new URLSearchParams(searchParams);
    next.delete('provider');
    next.delete('providerType');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestForProvider = (providerId: string) =>
    existingRequests.find(r => r.providerId === providerId);

  // Load existing requests
  useEffect(() => {
    requestIdempotencyKey.current = null;
    submissionInFlight.current = false;
    marketplaceApi.getProjectRequests(projectId).then(setExistingRequests).catch(() => {})
      .finally(() => setRequestsLoaded(true));
  }, [projectId]);

  // Both lists at once: "Experter i projektet" needs the profiles of either kind.
  useEffect(() => {
    marketplaceApi.getProviders('lawyer').then(setLawyers).catch(() => {});
    marketplaceApi.getProviders('inspector').then(setInspectors).catch(() => {});
  }, []);

  const selectProvider = (provider: any, type: 'lawyer' | 'inspector') => {
    setSelectedProvider(provider);
    setSelectedProviderType(type);
    setShowRequestForm(false);
    setRequestSent(false);
    requestIdempotencyKey.current = null;
    submissionInFlight.current = false;
  };

  useEffect(() => {
    const wanted = preselect.current;
    if (!wanted || !requestsLoaded) return;
    const candidates: Array<Lawyer | Inspector> = wanted.type === 'lawyer' ? lawyers : inspectors;
    const provider = candidates.find(item => item.id === wanted.id);
    if (!provider) return;
    preselect.current = null;
    setType(wanted.type);
    selectProvider(provider, wanted.type);
    if (!getRequestForProvider(provider.id)) {
      setShowRequestForm(true);
      return;
    }
    // A request is already out: show the actor's card with its invite button.
    window.setTimeout(() => document.getElementById(`provider-${provider.id}`)?.scrollIntoView?.({ block: 'start' }), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawyers, inspectors, requestsLoaded]);

  const handleInviteProvider = async (requestId: string) => {
    try {
      await marketplaceApi.inviteProvider(requestId);
      setMessage({ type: 'success', text: t('marketplace.invite.sent', 'Inbjudan skickad!') });
      // Reload requests to update status
      const updated = await marketplaceApi.getProjectRequests(projectId);
      setExistingRequests(updated);
    } catch {
      setMessage({ type: 'error', text: t('marketplace.invite.error', 'Det gick inte att skicka inbjudan') });
    }
  };

  const handleSubmitRequest = async () => {
    if (
      submissionInFlight.current ||
      !selectedProvider ||
      !selectedProviderType ||
      !requestForm.serviceType ||
      !requestForm.description
    ) return;
    submissionInFlight.current = true;
    const idempotencyKey = requestIdempotencyKey.current ?? crypto.randomUUID();
    requestIdempotencyKey.current = idempotencyKey;
    setIsSubmitting(true);
    setMessage(null);
    try {
      await marketplaceApi.createRequest({
        idempotencyKey,
        projectId,
        providerType: selectedProviderType,
        providerId: selectedProvider.id,
        serviceType: requestForm.serviceType,
        description: requestForm.description,
        timeframe: requestForm.timeframe
      });
      setRequestSent(true);
      setMessage({ type: 'success', text: t('marketplace.request.success') });
      // Existing requests are reloaded in the background (ClickUp 869fgxq45).
      void marketplaceApi.getProjectRequests(projectId)
        .then(setExistingRequests)
        .catch(error => console.error('Failed to reload requests:', error));
    } catch (error) {
      const responseData = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: { code?: unknown } } } }).response?.data
        : undefined;
      const errorCode = typeof responseData?.error?.code === 'string'
        ? responseData.error.code
        : null;
      const errorKey = errorCode === 'PROVIDER_REQUEST_INVALID_RECIPIENT' ||
        errorCode === 'PROVIDER_REQUEST_INVALID_ROLE' ||
        errorCode === 'PROVIDER_REQUEST_INVALID_PROFILE'
        ? 'marketplace.request.invalidRecipient'
        : errorCode === 'PROVIDER_REQUEST_IN_PROGRESS'
          ? 'marketplace.request.inProgress'
          : errorCode === 'PROVIDER_REQUEST_FINALIZATION_FAILED'
            ? 'marketplace.request.finalizationError'
            : 'marketplace.request.error';
      setRequestSent(false);
      // The same idempotency key is sent again, so a retry after a timeout
      // cannot create a second request.
      setMessage({ type: 'error', text: saveErrorText(t, error, errorKey) });
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };



  // The same expert card as on Extern hjälp (ClickUp 869fk17e6), with this
  // project's request as its status and the next step as its one action.
  const REQUEST_TONE: Record<string, ExpertCardStatus['tone']> = { pending: 'waiting', accepted: 'ok', invited: 'ok', declined: 'off' };
  const renderProviderCard = (layout: 'card' | 'row', type: 'lawyer' | 'inspector') => (provider: Lawyer | Inspector) => {
    const request = getRequestForProvider(provider.id);
    const expanded = expandedPresentation === provider.id;
    return (
      <ExpertCard
        key={provider.id}
        id={`provider-${provider.id}`}
        testId={`marketplace-provider-${provider.id}`}
        provider={provider}
        type={type}
        layout={layout}
        // Availability is for new work: not once the expert said yes here.
        showAvailability={!request || !['accepted', 'invited'].includes(request.status)}
        statuses={request ? [{
          key: request.id,
          label: t(`externalHelp.status.${request.status}`),
          tone: REQUEST_TONE[request.status] ?? 'waiting',
          meta: request.serviceType
        }] : []}
        action={!request ? {
          label: t('marketplace.provider.sendRequest'),
          icon: 'send',
          onClick: () => { selectProvider(provider, type); setShowRequestForm(true); },
          testId: `marketplace-send-request-${provider.id}`
        // Invite to the project only once the expert said yes: "Kopplad" then
        // always means they agreed, which is when their contact shows.
        } : request.status === 'accepted' ? {
          label: t('marketplace.steps.inviteToProject'),
          icon: 'user-plus',
          onClick: () => { void handleInviteProvider(request.id); },
          testId: `marketplace-invite-${provider.id}`
        } : undefined}
        expanded={expanded}
        onToggleProfile={() => setExpandedPresentation(expanded ? null : provider.id)}
      />
    );
  };

  // "Experter i projektet": everyone asked on this project, with this
  // project's state; acting on them is done on their card under "Hitta hjälp".
  const projectExperts = existingRequests
    .filter(request => request.status !== 'cancelled')
    .map(request => ({
      request,
      provider: ((request.providerType === 'lawyer' ? lawyers : inspectors) as Expert[]).find(item => item.id === request.providerId),
    }));

  return (
    <div className="external-help">
      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-0`} role="status">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label={t('common.close')}></button>
        </div>
      )}

      <section className="card external-help-section" aria-labelledby="project-external-help-mine" data-testid="project-external-help-mine">
        <div className="card-header">
          <h5 className="card-title mb-0" id="project-external-help-mine"><i className="feather-users me-2" aria-hidden="true"></i>{t('externalHelp.project.title')} ({projectExperts.length})</h5>
        </div>
        <p className="external-help-section-lead">{t('externalHelp.project.description')}</p>
        {projectExperts.length === 0 ? (
          <p className="external-help-empty">{t('externalHelp.project.empty')}</p>
        ) : (
          <ul className="external-help-mine-list">
            {projectExperts.map(({ request, provider }) => {
              const expanded = openProfile === request.id;
              const name = provider?.name ?? request.providerName ?? '';
              return (
                <li key={request.id} className="external-help-mine-row" data-testid={`project-expert-${request.providerId}`}>
                  <SavedExpertAvatar provider={provider} type={request.providerType} name={name} />
                  <div className="external-help-mine-who">
                    <p className="external-help-name">{name}</p>
                    <p className="external-help-meta">{[t(`externalHelp.role.${request.providerType}`), request.serviceType].filter(Boolean).join(' · ')}</p>
                    <ul className="external-help-mine-projects">
                      <li><span className={`external-help-pill is-${REQUEST_TONE[request.status] ?? 'waiting'}`}>{t(`externalHelp.status.${request.status}`)}</span></li>
                    </ul>
                  </div>
                  <div className="external-help-mine-end">
                    {provider && (
                      <button type="button" className="external-help-link" aria-expanded={expanded} onClick={() => setOpenProfile(expanded ? null : request.id)}>
                        {t(expanded ? 'externalHelp.hideProfile' : 'externalHelp.showProfile')}
                      </button>
                    )}
                  </div>
                  {expanded && provider && (
                    <div className="external-help-mine-profile">
                      <ExpertCard provider={provider} type={request.providerType} layout="card" showAvailability={false}
                        expanded onToggleProfile={() => setOpenProfile(null)} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ExpertFinder
        type={type}
        onTypeChange={setType}
        providers={type === 'lawyer' ? lawyers : inspectors}
        renderExpert={(provider, layout) => renderProviderCard(layout, type)(provider as Lawyer | Inspector)}
        idPrefix="project-external-help"
        heading="card"
      />

      {/* Request form — opens as a modal over the view. Closing (Avbryt/Escape/
          outside) keeps the draft in state so nothing typed is silently lost. */}
      {showRequestForm && selectedProvider && !requestSent && !getRequestForProvider(selectedProvider.id) && (
        <Modal
          title={`${t('marketplace.request.title')} — ${selectedProvider.name}`}
          onClose={() => setShowRequestForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowRequestForm(false)}>
                {t('common.cancel', 'Avbryt')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSubmitRequest} disabled={isSubmitting || !isProviderRequestDraftComplete(requestForm)}>
                {isSubmitting && <span className="spinner-border spinner-border-sm me-2"></span>}
                <i className="feather-send me-2"></i>
                {t('marketplace.request.submit')}
              </button>
            </>
          }
        >
          <ProviderRequestFields
            providerType={selectedProviderType === 'lawyer' ? 'lawyer' : 'inspector'}
            value={requestForm}
            onChange={setRequestForm}
          />
        </Modal>
      )}

    </div>
  );
}
